import { useEffect, useState } from 'react';

import { breakpoints } from '~constants/breakpoints';
import { BreakpointsPoints } from '~types/Breakpoints.types';

const MOBILE_BREAKPOINTS = [BreakpointsPoints.Xxs, BreakpointsPoints.Xs];
const TABLET_BREAKPOINTS = [BreakpointsPoints.Sm, BreakpointsPoints.Md];
const DESKTOP_BREAKPOINTS = [
  BreakpointsPoints.Lg,
  BreakpointsPoints.Xl,
  BreakpointsPoints.Xxl,
];
const DEFAULT_CONDITIONS = {
  isMobile: MOBILE_BREAKPOINTS,
  isTablet: TABLET_BREAKPOINTS,
  isDesktop: DESKTOP_BREAKPOINTS,
};

export function useBreakpoints<
  T extends string = keyof typeof DEFAULT_CONDITIONS,
>(props?: Record<T, BreakpointsPoints[]>) {
  const [current, setCurrent] = useState<BreakpointsPoints>();

  function handleChange(
    mediaList: {
      media: MediaQueryList;
      name: BreakpointsPoints;
    }[],
  ) {
    const matches = mediaList.filter(({ media }) => media.matches);

    if (matches.length) {
      setCurrent(matches[matches.length - 1].name);
    }
  }

  useEffect(() => {
    const mediaList = Object.entries(breakpoints).map(([name, minWidth]) => {
      const media = window.matchMedia(`(min-width: ${minWidth}px)`);

      media.onchange = () => handleChange(mediaList);

      return { name, media, minWidth } as {
        media: MediaQueryList;
        minWidth: number;
        name: BreakpointsPoints;
      };
    });

    mediaList.sort((a, b) => {
      return a.minWidth - b.minWidth;
    });

    handleChange(mediaList);

    return function () {
      mediaList.forEach(({ media }) => (media.onchange = null));
    };
  }, []);

  const conditions = Object.entries(props || DEFAULT_CONDITIONS).reduce(
    (result, [key, points]) => {
      result[key] = Boolean(current && points.includes(current));

      return result;
    },
    {} as Record<T, boolean>,
  );

  return [current, conditions] as const;
}
